import {
  type ILead,
  type ILeadActivity,
  type ILeadBasketProducts,
  type ILeadSessions,
  type IMessage,
  type IMessengerLead,
} from '@mahawi/eshop-common/src/types';
import { type IAdminEvent } from '@mahawi/eshop-rabbit';
import { EEventType } from '@mahawi/eshop-rabbit/message';
import { createSlice, type Reducer } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

import {
  requestFailedMatcher,
  resetLoadingOnRequestFailed,
} from '../async-state';
import { type IMessengerState, type LeadMessenger } from './types';

export const MIN_LEAD_SESSION_SECONDS = 8;

export const IDLE_LEAD_MAX_SECONDS = 15 * 60;

export const isLeadEngaged = (lead: LeadMessenger): boolean =>
  !!lead.hasMessages || !!lead.hasBasketProducts || !!lead.unread;

export enum EMessengerSystemMessage {
  OPENED_NEW_SESSION = '*** OPENED NEW SESSION ***',
}

export const preloadedState: IMessengerState = {
  inProcess: false,
  leads: undefined,
  lead: {
    uuid: undefined,
  },
  messages: [],
  sessions: undefined,
  basketProducts: undefined,
  message: '',
  lastMessage: {
    uuid: '',
  },
  updatedAt: undefined,
  pendingLeads: {},
};

const sortLeads = (leads: LeadMessenger[]): LeadMessenger[] => {
  return leads.sort((a: LeadMessenger, b: LeadMessenger): number => {
    const aActive: boolean = !!a.isActive;
    const bActive: boolean = !!b.isActive;
    const aUnread: boolean = !!a.unread;
    const bUnread: boolean = !!b.unread;

    if (aActive !== bActive) {
      return bActive ? 1 : -1;
    }

    if (aActive && bActive) {
      return b.createdAt - a.createdAt;
    }

    if (aUnread !== bUnread) {
      return bUnread ? 1 : -1;
    }

    return b.lastActivity - a.lastActivity;
  });
};

const sortMessages = (messages: IMessage[]): IMessage[] =>
  messages.sort(
    (a: IMessage, b: IMessage): number => b.timestamp - a.timestamp,
  );

const addLead = (
  state: IMessengerState,
  leadUUID: string,
  createdAt: number = dayjs().unix(),
): void => {
  if (!state.leads) {
    return;
  }

  const lead: LeadMessenger | undefined = state.leads.find(
    (l: LeadMessenger): boolean => l.uuid === leadUUID,
  );

  if (lead) {
    return;
  }

  state.leads.push({
    uuid: leadUUID,
    unread: 0,
    lastActivity: dayjs().unix(),
    tick: dayjs().unix(),
    createdAt,
  });

  state.leads = sortLeads(state.leads);
};

const setLeadUnread = (
  state: IMessengerState,
  leadUUID: string,
  unread: number,
): void => {
  if (!state.leads) {
    return;
  }

  const lead: LeadMessenger | undefined = state.leads.find(
    (l: LeadMessenger): boolean => l.uuid === leadUUID,
  );

  if (lead) {
    if (lead.unread) {
      if (unread === 0) {
        lead.unread = 0;
      } else {
        lead.unread += unread;
      }
    } else {
      lead.unread = unread;
    }
  } else {
    addLead(state, leadUUID);
    setLeadUnread(state, leadUUID, unread);
  }
};

const setLeadLastActivity = (
  state: IMessengerState,
  leadUUID: string,
  timestamp: number,
  isActive = false,
): void => {
  if (!state.leads) {
    return;
  }

  const lead: LeadMessenger | undefined = state.leads.find(
    (l: LeadMessenger): boolean => l.uuid === leadUUID,
  );

  if (lead) {
    lead.tick = timestamp;
    lead.lastActivity = lead.tick;
    lead.isActive = isActive;
  }
};

const findLead = (
  state: IMessengerState,
  leadUUID: string,
): LeadMessenger | undefined =>
  state.leads?.find((l: LeadMessenger): boolean => l.uuid === leadUUID);

const messengerSlice = createSlice({
  name: 'Messenger',
  initialState: preloadedState,
  reducers: {
    loadLeads: {
      reducer(state) {
        state.inProcess = true;
        state.leads = undefined;
      },
      prepare(hours: number) {
        return { payload: { hours } };
      },
    },
    loadLeadsResponse: {
      reducer(state, action: { payload: { leads: IMessengerLead[] } }) {
        state.inProcess = false;
        state.leads = sortLeads(action.payload.leads);
        state.updatedAt = Date.now();
      },
      prepare(leads: IMessengerLead[]) {
        return { payload: { leads } };
      },
    },
    setActiveLead: {
      reducer(state, action: { payload: { lead: { uuid: string } } }) {
        setLeadUnread(state, action.payload.lead.uuid, 0);
        state.inProcess = true;
        state.messages = [];
        state.sessions = undefined;
        state.basketProducts = undefined;
        state.lead = { uuid: action.payload.lead.uuid };
        state.message = '';
      },
      prepare(uuid: string) {
        return { payload: { lead: { uuid } } };
      },
    },
    setActiveLeadResponse: {
      reducer(
        state,
        action: { payload: { lead: ILead; messages: IMessage[] } },
      ) {
        state.inProcess = false;
        state.messages = sortMessages(action.payload.messages);
        state.updatedAt = Date.now();
      },
      prepare(lead: ILead, messages: IMessage[]) {
        return { payload: { lead, messages } };
      },
    },
    sendMessage: {
      reducer(
        state,
        action: { payload: { message: string; lead: { uuid: string } } },
      ) {
        state.inProcess = true;
        state.message = action.payload.message;
      },
      prepare(uuid: string, message: string) {
        return { payload: { message, lead: { uuid } } };
      },
    },
    sendMessageResponse: {
      reducer(
        state,
        action: { payload: { message: IMessage; saved: boolean } },
      ) {
        if (action.payload.message) {
          state.messages.push(action.payload.message);
        }
        state.inProcess = false;
        state.message = '';
        state.messages = sortMessages(state.messages);
        state.updatedAt = Date.now();
      },
      prepare(message: IMessage, saved: boolean) {
        return { payload: { message, saved } };
      },
    },
    adminConnected: {
      reducer() {},
      prepare(uuid: string) {
        return { payload: { admin: { uuid } } };
      },
    },
    adminConnectedResponse: {
      reducer() {},
      prepare(set: boolean) {
        return { payload: { set } };
      },
    },
    adminDisconnected: {
      reducer() {},
      prepare(uuid: string) {
        return { payload: { admin: { uuid } } };
      },
    },
    adminDisconnectedResponse: {
      reducer() {},
      prepare(set: boolean) {
        return { payload: { set } };
      },
    },
    customerSendMessage: {
      reducer(
        state,
        action: { payload: { message: IMessage; lead: { uuid: string } } },
      ) {
        state.updatedAt = Date.now();
        const leadUUID: string = action.payload.lead.uuid;
        const isOpenedNewSession: boolean =
          action.payload.message.message ===
          EMessengerSystemMessage.OPENED_NEW_SESSION;
        const isLeadListed: boolean = !!state.leads?.some(
          (l: LeadMessenger): boolean => l.uuid === leadUUID,
        );

        if (isOpenedNewSession && !isLeadListed) {
          state.pendingLeads[leadUUID] = action.payload.message.timestamp;
          return;
        }

        if (!isOpenedNewSession) {
          delete state.pendingLeads[leadUUID];
        }

        addLead(state, leadUUID);
        setLeadLastActivity(
          state,
          leadUUID,
          action.payload.message.timestamp,
          true,
        );
        if (isOpenedNewSession) {
          return;
        }
        const lead: LeadMessenger | undefined = findLead(state, leadUUID);
        if (lead) {
          lead.hasMessages = true;
        }
        if (state.lead.uuid === action.payload.lead.uuid) {
          state.messages.push(action.payload.message);
          sortMessages(state.messages);
        }
        setLeadUnread(state, action.payload.lead.uuid, 1);
        state.lastMessage = { uuid: action.payload.message.uuid };
      },
      prepare(uuid: string, message: IMessage) {
        return { payload: { message, lead: { uuid } } };
      },
    },
    leadSessions: {
      reducer(state, action: { payload: ILeadSessions }) {
        if (state.lead.uuid === action.payload.lead.uuid) {
          state.sessions = action.payload.sessions;
        }
        state.updatedAt = Date.now();
      },
      prepare(sessions: ILeadSessions) {
        return { payload: { ...sessions } };
      },
    },
    leadBasketProducts: {
      reducer(state, action: { payload: ILeadBasketProducts }) {
        if (state.lead.uuid === action.payload.lead.uuid) {
          state.basketProducts = action.payload.basketProducts;
        }
        const lead: LeadMessenger | undefined = findLead(
          state,
          action.payload.lead.uuid,
        );
        const hasBasketProducts: boolean =
          action.payload.basketProducts.length > 0;
        if (lead && !!lead.hasBasketProducts !== hasBasketProducts) {
          lead.hasBasketProducts = hasBasketProducts;
        }
      },
      prepare(basketProducts: ILeadBasketProducts) {
        return { payload: { ...basketProducts } };
      },
    },
    leadActivity: {
      reducer(state, action: { payload: ILeadActivity }) {
        const leadUUID: string = action.payload.lead.uuid;
        const { hasBasketProducts } = action.payload;

        if (!findLead(state, leadUUID) && hasBasketProducts && state.leads) {
          addLead(state, leadUUID, state.pendingLeads[leadUUID]);
          delete state.pendingLeads[leadUUID];
        }

        const lead: LeadMessenger | undefined = findLead(state, leadUUID);
        if (lead && !!lead.hasBasketProducts !== hasBasketProducts) {
          lead.hasBasketProducts = hasBasketProducts;
        }
      },
      prepare(activity: ILeadActivity) {
        return { payload: { ...activity } };
      },
    },
    pruneLeads: {
      reducer(
        state,
        action: { payload: { now: number; engagedLeadMaxIdleSeconds: number } },
      ) {
        const { now, engagedLeadMaxIdleSeconds } = action.payload;

        Object.entries(state.pendingLeads).forEach(
          ([leadUUID, openedAt]: [string, number]): void => {
            if (now - openedAt > IDLE_LEAD_MAX_SECONDS) {
              delete state.pendingLeads[leadUUID];
            }
          },
        );

        if (!state.leads) {
          return;
        }

        const keptLeads: LeadMessenger[] = state.leads.filter(
          (lead: LeadMessenger): boolean => {
            if (lead.uuid === state.lead.uuid || lead.unread) {
              return true;
            }

            const maxIdleSeconds: number = isLeadEngaged(lead)
              ? engagedLeadMaxIdleSeconds
              : IDLE_LEAD_MAX_SECONDS;

            return now - lead.lastActivity <= maxIdleSeconds;
          },
        );

        if (keptLeads.length !== state.leads.length) {
          state.leads = keptLeads;
        }
      },
      prepare(now: number, engagedLeadMaxIdleSeconds: number) {
        return { payload: { now, engagedLeadMaxIdleSeconds } };
      },
    },
    customerTick: {
      reducer(state, action: { payload: IAdminEvent }) {
        const leadUUID: string = action.payload.lead.uuid;
        const newIsActive: boolean =
          action.payload.event.type === EEventType.UPDATE;
        const pendingOpenedAt: number | undefined =
          state.pendingLeads[leadUUID];

        if (pendingOpenedAt !== undefined) {
          const isSessionLongEnough: boolean =
            action.payload.timestamp - pendingOpenedAt >=
            MIN_LEAD_SESSION_SECONDS;

          if (isSessionLongEnough && state.leads) {
            addLead(state, leadUUID, pendingOpenedAt);
            delete state.pendingLeads[leadUUID];
          } else if (action.payload.event.type === EEventType.DISCONNECT) {
            delete state.pendingLeads[leadUUID];
          }
        }

        const lead: LeadMessenger | undefined = state.leads?.find(
          (l: LeadMessenger): boolean => l.uuid === leadUUID,
        );
        const isActiveChanged: boolean = lead?.isActive !== newIsActive;

        setLeadLastActivity(
          state,
          leadUUID,
          action.payload.timestamp,
          newIsActive,
        );
        state.updatedAt = Date.now();

        // Sort when isActive transitions (active ↔ inactive) OR when lead stays
        // inactive (lastActivity/unread can shift its position among inactive leads).
        // Skip sort only for active→active ticks — createdAt is the sort key there
        // and it never changes, so already-sorted active leads stay in correct order.
        if (state.leads && (isActiveChanged || !newIsActive)) {
          state.leads = sortLeads(state.leads);
        }
      },
      prepare(event: IAdminEvent) {
        return { payload: { ...event } };
      },
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      requestFailedMatcher('Messenger'),
      resetLoadingOnRequestFailed,
    );
  },
});

export const {
  loadLeads,
  loadLeadsResponse,
  setActiveLead,
  setActiveLeadResponse,
  sendMessage,
  sendMessageResponse,
  adminConnected,
  adminConnectedResponse,
  adminDisconnected,
  adminDisconnectedResponse,
  customerSendMessage,
  leadSessions,
  leadBasketProducts,
  leadActivity,
  pruneLeads,
  customerTick,
} = messengerSlice.actions;
const reducer: Reducer<IMessengerState> = messengerSlice.reducer;

export default reducer;
